/// <amd-module name="Wit/UI/Components/WorkItemGridCell/WorkItemGridCell" />
import * as React from "react";

import { dateToString } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Tooltip } from "VSSUI/TooltipEx";

import { FieldType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WorkItemStateProvider } from "Wit/UI/Data";
import { IdentityDisplayMode } from "Wit/UI/Enums";
import { IdentityView } from "Wit/UI/IdentityView";
import * as Resources from "Wit/UI/Resources";
import { TagsList } from "Wit/UI/TagsList";
import { WorkItemState } from "Wit/UI/WorkItemState";
import { WorkItemSmallView } from "Wit/UI/WorkItemViews";

import { formatHtmlToPlainText } from "../../Utilities/HtmlFormatter";
import { IWorkItemGridCellProps } from "./WorkItemGridCell.props";

export class WorkItemGridCell extends VssComponent<IWorkItemGridCellProps> {
    public render(): JSX.Element | null {
        const { workItem, dataProvider, fieldName, fieldType } = this.props;

        if (!workItem) {
            return null;
        }

        let cellContent: JSX.Element | null = null;
        let fieldValue = workItem.fields[fieldName];
        if (fieldName === CoreFieldRefNames.Title) {
            cellContent = <WorkItemSmallView dataProvider={dataProvider} data={workItem} showId={false} showTitleLink={true} />;
        } else if (fieldName === CoreFieldRefNames.State) {
            cellContent = (
                <WorkItemStateProvider dataProvider={dataProvider} dataSource={workItem}>
                    {stateAndColor => <WorkItemState data={stateAndColor} hideText={false} />}
                </WorkItemStateProvider>
            );
        } else if (fieldName === CoreFieldRefNames.Tags) {
            const tagsString = workItem.fields[fieldName] as string;
            if (tagsString) {
                const tags = tagsString.split(";").map(item => item.trim());
                cellContent = <TagsList className={"work-item-grid-tags"} tags={tags} />;
            }
        } else if (fieldType === FieldType.Html || fieldType === FieldType.History) {
            fieldValue = formatHtmlToPlainText(fieldValue);
            cellContent = renderWorkItemCellContent(fieldValue);
        } else if (fieldValue && fieldValue.displayName !== undefined) {
            // Handles identity fields
            cellContent = <IdentityView identityRef={fieldValue} viewMode={IdentityDisplayMode.NameOnly} displayTooltipOnHover={true} />;
        } else if (fieldName === CoreFieldRefNames.Parent && Object.keys(workItem.fields).length > 0 && workItem.fields[CoreFieldRefNames.Parent]) {
            // Handle parent item view
            const parentWorkItem = workItem.fields[CoreFieldRefNames.Parent];

            if (parentWorkItem.fetchError) {
                cellContent = (
                    <Icon
                        className={"icon-large-margin error-text-important font-weight-semibold"}
                        iconName="AlertSolid"
                        tooltipProps={{ text: parentWorkItem.fetchError }}
                    />
                );
            } else {
                cellContent = <WorkItemSmallView dataProvider={dataProvider} data={parentWorkItem} showId={false} showTitleLink={true} />;
            }
        } else {
            if (fieldName === CoreFieldRefNames.Id) {
                fieldValue = workItem.id;
            } else if (fieldValue instanceof Date) {
                fieldValue = dateToString(fieldValue, true);
            }

            if (fieldType === FieldType.Boolean || typeof fieldValue === "boolean") {
                // needed because evaluated boolean under JSX appears as empty string
                fieldValue = fieldValue ? Resources.ValueTrue : Resources.ValueFalse;
            }
            cellContent = renderWorkItemCellContent(fieldValue);
        }

        return cellContent;
    }
}

/** Provides core rendering behavior for rendering a work item cell. Can be reused for custom render implementations. */
export function renderWorkItemCellContent(fieldValue: string) {
    return (
        <Tooltip overflowOnly={true} text={fieldValue}>
            <div className="text-ellipsis">{fieldValue != null ? fieldValue : ""}</div>
        </Tooltip>
    );
}
